import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const LINKS = [
  ["overview", "overview"], ["gallery", "gallery"], ["amenities", "amenities"], ["memberships", "memberships"],
  ["availability", "availability"], ["how-it-works", "how"], ["location", "location"], ["faq", "faq"], ["contact", "contact"], ["our-story", "story"],
];

export function LangToggle({ id = "lang-toggle-button" }) {
  const { lang, setLang } = useLang();
  return (
    <button data-testid={id} onClick={() => setLang(lang === "fr" ? "en" : "fr")}
      className="rounded-full border border-white/15 px-3 py-1.5 font-mono text-xs text-slate-300 transition-colors hover:border-white/40 hover:text-white">
      <span className={lang === "fr" ? "text-white" : "text-slate-500"}>FR</span> / <span className={lang === "en" ? "text-white" : "text-slate-500"}>EN</span>
    </button>
  );
}

export function Logo({ className = "h-9" }) {
  return <img src="/brand/logo-wide.jpg" alt="RECORDING ROOM" className={`${className} w-auto object-contain`} />;
}

function AccountLink({ onClick }) {
  const { t } = useLang();
  const { user } = useAuth();
  const to = user ? (user.role === "admin" ? "/admin" : "/member") : "/login";
  const label = user ? (user.role === "admin" ? t("nav.admin") : t("nav.account")) : t("nav.login");
  return <Link to={to} onClick={onClick} data-testid="nav-login-btn" className="text-sm text-slate-300 transition-colors hover:text-white">{label}</Link>;
}

export default function Navbar({ home = true }) {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const href = (id) => (home ? `#${id}` : `/#${id}`);
  const book = () => {
    setOpen(false);
    if (user) return nav(user.role === "admin" ? "/admin" : "/member");
    const el = document.getElementById("memberships");
    if (el) el.scrollIntoView({ behavior: "smooth" }); else nav("/#memberships");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-black/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="shrink-0"><Logo /></Link>
        <nav className="hidden items-center gap-5 xl:flex">
          {[...LINKS.slice(0, 7), LINKS[9]].map(([id, k]) => (
            <a key={id} href={href(id)} data-testid={`nav-link-${id}`} className="text-sm text-slate-400 transition-colors hover:text-white">{t(`nav.${k}`)}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <div className="hidden sm:block"><AccountLink /></div>
          {user && <button onClick={logout} data-testid="nav-logout-btn" className="hidden items-center gap-1.5 text-sm text-slate-300 hover:text-white sm:inline-flex"><LogOut size={15} />{lang === "fr" ? "Se déconnecter" : "Log Out"}</button>}
          <button onClick={book} data-testid="nav-book-session-btn" className="btn-red hidden rounded-full px-4 py-2 text-sm font-semibold sm:inline-flex">{t("nav.book")}</button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="rounded-full border border-white/15 p-2 text-slate-200 xl:hidden" data-testid="nav-mobile-menu-btn" aria-label="Menu"><Menu size={18} /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm border-white/10 bg-[#0b0b0e]">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-8 flex flex-col gap-1">
                {LINKS.map(([id, k]) => (
                  <a key={id} href={href(id)} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${id}`}
                    className="rounded-lg px-3 py-3 text-base text-slate-200 hover:bg-white/5">{t(`nav.${k}`)}</a>
                ))}
                <div className="my-4 h-px bg-white/10" />
                <div className="px-3 py-2"><AccountLink onClick={() => setOpen(false)} /></div>
                <button onClick={book} data-testid="mobile-nav-book-btn" className="btn-red mt-3 rounded-full px-4 py-3 font-semibold">{t("nav.book")}</button>
                {user && <button onClick={() => { setOpen(false); logout(); }} data-testid="mobile-nav-logout-btn" className="mt-3 flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-3 text-slate-200 hover:border-red-500"><LogOut size={16} />{lang === "fr" ? "Se déconnecter" : "Log Out"}</button>}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
